import type { BookingFilters } from './bookings';
import type {
  EmployeeFilters,
  GoodsReceiptFilters,
  ItemFilters,
  ItemUnitFilters,
  VendorFilters,
} from './masters';
import type { GarmentJobKind } from '../generated/enums';
import type { PurchaseBillFilters } from './purchaseBills';
import type { PurchaseOrderFilters } from './purchaseOrders';
import type { SaleGarmentQuery, SaleInvoiceFilters } from './saleInvoices';
import type { VendorPaymentFilters } from './vendorPayments';

/**
 * The URLs the read endpoints are asked through — one definition for the web's hooks and the
 * phone's alike.
 *
 * <p>
 * The hooks themselves stay in each client (TanStack's keys and options are a client's business);
 * the question put to the server does not. Two copies of "today's pickups" is how a phone ends up
 * counting differently from the screen beside it, so each client builds its query string here.
 * Optional parameters are left out rather than sent empty: the server reads an absent filter as
 * "no filter", and an empty one as a value.
 * </p>
 */

function withParams(path: string, params: URLSearchParams): string {
  const query = params.toString();

  return query ? `${path}?${query}` : path;
}

/** `GET /api/booking` — the register, and every count on the morning screen. */
export function bookingsPath(filters: BookingFilters = {}): string {
  const {
    stage,
    customerId,
    overdue,
    pickupsOn,
    returnsOn,
    inAlteration,
    takenFrom,
    takenTo,
    search,
    list,
    dayFrom,
    dayTo,
    page = 1,
    pageSize = 25,
  } = filters;

  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  const optional: Record<string, string | undefined> = {
    list,
    stage,
    customerId: customerId === undefined ? undefined : String(customerId),
    overdue: overdue === undefined ? undefined : String(overdue),
    pickupsOn,
    returnsOn,
    inAlteration: inAlteration === undefined ? undefined : String(inAlteration),
    takenFrom,
    takenTo,
    search: search || undefined,
    dayFrom,
    dayTo,
  };

  for (const [key, value] of Object.entries(optional)) {
    if (value !== undefined) {
      params.set(key, value);
    }
  }

  return withParams('/api/booking', params);
}

/** `GET /api/cash/position` — the balance as at `to`, and what moved over the window. */
export function cashPositionPath(from?: string, to?: string): string {
  const params = new URLSearchParams();

  if (from) {
    params.set('from', from);
  }

  if (to) {
    params.set('to', to);
  }

  return withParams('/api/cash/position', params);
}

export interface CashLedgerFilters {
  accountId?: number | undefined;
  from?: string | undefined;
  to?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

function cashListPath(path: string, filters: CashLedgerFilters): string {
  const { accountId, from, to, page = 1, pageSize = 25 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (accountId !== undefined) {
    params.set('accountId', String(accountId));
  }

  if (from) {
    params.set('from', from);
  }

  if (to) {
    params.set('to', to);
  }

  return withParams(path, params);
}

/** `GET /api/cash/ledger` — every movement, paged; the cash book and the phone's Today list. */
export function cashLedgerPath(filters: CashLedgerFilters = {}): string {
  return cashListPath('/api/cash/ledger', filters);
}

/** `GET /api/cash/counts` — the day-end counts, paged. */
export function cashCountsPath(filters: CashLedgerFilters = {}): string {
  return cashListPath('/api/cash/counts', filters);
}

/** `GET /api/cash/transfers` — money moved between the branch's accounts, newest first. */
export function cashTransfersPath(
  filters: { includeVoided?: boolean | undefined; page?: number; pageSize?: number } = {},
): string {
  const { includeVoided = false, page = 1, pageSize = 10 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (includeVoided) {
    params.set('includeVoided', 'true');
  }

  return withParams('/api/cash/transfers', params);
}

/** `GET /api/cash/transfers/in-transit` — money between branches, outgoing and incoming. */
export function cashInTransitPath(): string {
  return '/api/cash/transfers/in-transit';
}

/** `GET /api/cash/vouchers` — owner's money, drawings and other income, newest first. */
export function cashVouchersPath(
  filters: { includeVoided?: boolean | undefined; page?: number; pageSize?: number } = {},
): string {
  const { includeVoided = false, page = 1, pageSize = 10 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (includeVoided) {
    params.set('includeVoided', 'true');
  }

  return withParams('/api/cash/vouchers', params);
}

/** `GET /api/expense/recurring` — the branch's recurring expenses, retired ones only when asked. */
export function recurringExpensesPath(includeInactive = false): string {
  return includeInactive ? '/api/expense/recurring?includeInactive=true' : '/api/expense/recurring';
}

/** `GET /api/expense/recurring/due` — what is due, overdue or coming up within the week. */
export function dueExpensesPath(): string {
  return '/api/expense/recurring/due';
}

/** `GET /api/cash/accounts/{id}/day-book` — one account's day, for the day book print. */
export function cashDayBookPath(accountId: number, date: string): string {
  return withParams(`/api/cash/accounts/${accountId}/day-book`, new URLSearchParams({ date }));
}

/** `GET /api/cash/accounts/{id}/reconciliation-draft` — what a bank's reconcile page starts from. */
export function bankReconciliationDraftPath(accountId: number, statementDate: string): string {
  return withParams(
    `/api/cash/accounts/${accountId}/reconciliation-draft`,
    new URLSearchParams({ statementDate }),
  );
}

/** `GET /api/cash/reconciliations` — statements already matched, newest first. */
export function bankReconciliationsPath(
  filters: {
    accountId?: number | undefined;
    includeVoided?: boolean | undefined;
    page?: number;
    pageSize?: number;
  } = {},
): string {
  const { accountId, includeVoided = false, page = 1, pageSize = 10 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (accountId !== undefined) {
    params.set('accountId', String(accountId));
  }

  if (includeVoided) {
    params.set('includeVoided', 'true');
  }

  return withParams('/api/cash/reconciliations', params);
}

/** `GET /api/employee` — the staff register; the phone's Staff and users reads one large page. */
export function employeesPath(filters: EmployeeFilters = {}): string {
  const { search, capability, includeInactive = false, page = 1, pageSize = 25 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (search) {
    params.set('search', search);
  }

  if (capability) {
    params.set('capability', capability);
  }

  if (includeInactive) {
    params.set('includeInactive', 'true');
  }

  return withParams('/api/employee', params);
}

export interface ExpenseFilters {
  from?: string | undefined;
  to?: string | undefined;
  headId?: number | undefined;
  includeVoided?: boolean | undefined;
  /** Number, notes, reference or head name. */
  search?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

/** `GET /api/expense` — vouchers; the morning screen reads only `total`. */
/** `GET /api/vendor-payment` — payments to vendors, newest first. */
export function vendorPaymentsPath(filters: VendorPaymentFilters = {}): string {
  const { vendorId, includeCancelled = false, page = 1, pageSize = 25 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (vendorId !== undefined) {
    params.set('vendorId', String(vendorId));
  }

  if (includeCancelled) {
    params.set('includeCancelled', 'true');
  }

  return withParams('/api/vendor-payment', params);
}

/** `GET /api/vendor-payment/{id}` — one payment with what it was set against. */
export function vendorPaymentPath(id: number): string {
  return `/api/vendor-payment/${id}`;
}

/** `GET /api/sale-invoice` — retail sales, newest first. */
export function saleInvoicesPath(filters: SaleInvoiceFilters = {}): string {
  const { search, status, from, to, page = 1, pageSize = 25 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (search?.trim()) {
    params.set('search', search.trim());
  }

  if (status !== undefined) {
    params.set('status', status);
  }

  if (from) {
    params.set('from', from);
  }

  if (to) {
    params.set('to', to);
  }

  return withParams('/api/sale-invoice', params);
}

/** `GET /api/sale-invoice/{id}` — one sale with its lines and payments. */
export function saleInvoicePath(id: number): string {
  return `/api/sale-invoice/${id}`;
}

/** `GET /api/sale-invoice/garment?serial=` — a scanned garment, and whether it can be sold. */
export function sellableGarmentPath(serial: string): string {
  return withParams('/api/sale-invoice/garment', new URLSearchParams({ serial }));
}

/** `GET /api/sale-invoice/garments` — garments to sell, by short name and digits or by name. */
export function saleGarmentsPath(query: SaleGarmentQuery): string {
  const params = new URLSearchParams();

  if (query.designCode?.trim()) {
    params.set('designCode', query.designCode.trim());
    params.set('digits', (query.digits ?? '').replace(/\D/g, ''));
  } else if (query.search?.trim()) {
    params.set('search', query.search.trim());
  }

  params.set('take', String(query.take ?? 20));

  return withParams('/api/sale-invoice/garments', params);
}

/** `GET /api/sale-invoice/item-suggestions` — other items sold before, by name. */
export function saleItemSuggestionsPath(search: string): string {
  return withParams(
    '/api/sale-invoice/item-suggestions',
    new URLSearchParams({ search: search.trim() }),
  );
}

/** `GET /api/sale-invoice/day` — one day's selling at the branch. */
export function saleDayPath(date: string): string {
  return withParams('/api/sale-invoice/day', new URLSearchParams({ date }));
}

/** `GET /api/sale-invoice/counter` — the discount ceiling here, and whether the branch bills without GST. */
export function saleCounterPath(): string {
  return '/api/sale-invoice/counter';
}

/** `GET /api/vendor-payment/accounts` — what a payment can be paid from; names, no balances. */
export function vendorPaymentAccountsPath(): string {
  return '/api/vendor-payment/accounts';
}

/** `GET /api/booking/payment-accounts` — what a booking's money may go into; names, no balances. */
export function bookingPaymentAccountsPath(): string {
  return '/api/booking/payment-accounts';
}

/** `GET /api/sale-invoice/accounts` — what a sale's money may go into; names, no balances. */
export function saleAccountsPath(): string {
  return '/api/sale-invoice/accounts';
}

/** `GET /api/expense/accounts` — what a voucher can be paid from; names, no balances. */
export function expenseAccountsPath(): string {
  return '/api/expense/accounts';
}

/** `GET /api/vendor-payment/ledger/{vendorId}` — what a vendor is owed, oldest first. */
export function vendorLedgerPath(vendorId: number): string {
  return `/api/vendor-payment/ledger/${vendorId}`;
}

/** `GET /api/outstanding/receive` — what customers owe the acting branch, longest owed first. */
export function receivablesPath(): string {
  return '/api/outstanding/receive';
}

/** `GET /api/outstanding/pay` — what the acting branch owes vendors, most overdue first. */
export function payablesPath(): string {
  return '/api/outstanding/pay';
}

/** `GET /api/purchase-bill` — suppliers' bills, newest first; cancelled ones only when asked. */
export function purchaseBillsPath(filters: PurchaseBillFilters = {}): string {
  const { search, supplierVendorId, includeCancelled = false, page = 1, pageSize = 25 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (search) {
    params.set('search', search);
  }

  if (supplierVendorId !== undefined) {
    params.set('supplierVendorId', String(supplierVendorId));
  }

  if (includeCancelled) {
    params.set('includeCancelled', 'true');
  }

  return withParams('/api/purchase-bill', params);
}

/** `GET /api/purchase-bill/{id}` — one bill with its lines, totals and differences. */
export function purchaseBillPath(id: number): string {
  return `/api/purchase-bill/${id}`;
}

/** `GET /api/purchase-bill/billable` — what a supplier has delivered and not yet billed. */
export function billablePath(supplierVendorId: number): string {
  return `/api/purchase-bill/billable?supplierVendorId=${supplierVendorId}`;
}

/** `GET /api/purchase-desk` — suppliers with something waiting; `all` lists every supplier. */
export function purchaseDeskPath(all: boolean): string {
  return all ? '/api/purchase-desk?all=true' : '/api/purchase-desk';
}

/** `GET /api/purchase-desk/{vendorId}` — one supplier's steps and latest papers. */
export function supplierDeskPath(vendorId: number): string {
  return `/api/purchase-desk/${vendorId}`;
}

/** `GET /api/purchase-order` — the acting branch's orders, newest first. */
export function purchaseOrdersPath(filters: PurchaseOrderFilters = {}): string {
  const {
    search,
    supplierVendorId,
    openOnly = false,
    status,
    from,
    to,
    page = 1,
    pageSize = 25,
  } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (search) {
    params.set('search', search);
  }

  if (supplierVendorId !== undefined) {
    params.set('supplierVendorId', String(supplierVendorId));
  }

  if (openOnly) {
    params.set('openOnly', 'true');
  }

  if (status) {
    params.set('status', status);
  }

  if (from) {
    params.set('from', from);
  }

  if (to) {
    params.set('to', to);
  }

  return withParams('/api/purchase-order', params);
}

/** `GET /api/purchase-order/glance` — the open orders in four figures. */
export const purchaseOrderGlancePath = '/api/purchase-order/glance';

/** `GET /api/purchase-order/{id}` — one order with its lines and receipts. */
export function purchaseOrderPath(id: number): string {
  return `/api/purchase-order/${id}`;
}

/** `GET /api/goods-receipt` — the acting branch's deliveries, newest first. */
export function goodsReceiptsPath(filters: GoodsReceiptFilters = {}): string {
  const { search, supplierVendorId, from, to, page = 1, pageSize = 25 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (search) {
    params.set('search', search);
  }

  if (supplierVendorId !== undefined) {
    params.set('supplierVendorId', String(supplierVendorId));
  }

  if (from) {
    params.set('from', from);
  }

  if (to) {
    params.set('to', to);
  }

  return withParams('/api/goods-receipt', params);
}

/** `GET /api/goods-receipt/{id}` — one delivery, for its page and printed note. */
export function goodsReceiptPath(id: number): string {
  return `/api/goods-receipt/${id}`;
}

export function expensesPath(filters: ExpenseFilters = {}): string {
  const { from, to, headId, includeVoided = false, search, page = 1, pageSize = 25 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (from) {
    params.set('from', from);
  }

  if (to) {
    params.set('to', to);
  }

  if (headId !== undefined) {
    params.set('headId', String(headId));
  }

  if (includeVoided) {
    params.set('includeVoided', 'true');
  }

  if (search && search.trim() !== '') {
    params.set('search', search.trim());
  }

  return withParams('/api/expense', params);
}

/** `GET /api/expense/summary` — what was spent between two days, by head and by account. */
export function expenseSummaryPath(from: string, to: string): string {
  return withParams('/api/expense/summary', new URLSearchParams({ from, to }));
}

/** `GET /api/return-inspection/pending` — garments back and not yet looked at. */
export function pendingInspectionsPath(includeInspected = false): string {
  return `/api/return-inspection/pending${includeInspected ? '?includeInspected=true' : ''}`;
}

/** `GET /api/garment-job` — one kind's queue, open jobs only unless asked. */
export function garmentJobsPath(kind: GarmentJobKind, includeClosed = false): string {
  const params = new URLSearchParams({ kind });

  if (includeClosed) {
    params.set('includeClosed', 'true');
  }

  return withParams('/api/garment-job', params);
}

/** `GET /api/vendor` — the vendor master, and the launderer or tailor a dispatch goes to. */
export function vendorsPath(filters: VendorFilters = {}): string {
  const { search, capability, includeInactive = false, page = 1, pageSize = 25 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (search) {
    params.set('search', search);
  }

  if (capability) {
    params.set('capability', capability);
  }

  if (includeInactive) {
    params.set('includeInactive', 'true');
  }

  return withParams('/api/vendor', params);
}

/** `GET /api/item-unit` — the garment register, and the find's unit lookup. */
export function itemUnitsPath(filters: ItemUnitFilters = {}): string {
  const { itemId, variantId, status, search, page = 1, pageSize = 25 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (itemId !== undefined) {
    params.set('itemId', String(itemId));
  }

  if (variantId !== undefined) {
    params.set('variantId', String(variantId));
  }

  if (status) {
    params.set('status', status);
  }

  if (search) {
    params.set('search', search);
  }

  return withParams('/api/item-unit', params);
}

/**
 * `GET /api/item-unit/reference-numbers` — the next shop's number, and which of these (already
 * cleaned, core `referencesToCheck`) a garment carries.
 */
export function referenceNumbersPath(numbers: readonly string[]): string {
  return numbers.length === 0
    ? '/api/item-unit/reference-numbers'
    : withParams(
        '/api/item-unit/reference-numbers',
        new URLSearchParams({ numbers: numbers.join(',') }),
      );
}

/** `GET /api/item-unit/{id}/timeline` — one garment's life, every track merged. */
export function garmentTimelinePath(unitId: number): string {
  return `/api/item-unit/${unitId}/timeline`;
}

/** `GET /api/booking/{id}` — one booking, every garment and payment on it. */
export function bookingPath(id: number): string {
  return `/api/booking/${id}`;
}

/** `GET /api/tax-document/booking/{id}` — every invoice and note a booking has raised. */
export function bookingTaxDocumentsPath(bookingId: number): string {
  return `/api/tax-document/booking/${bookingId}`;
}

/** `GET /api/branch/{id}/letterhead` — the shop's name, address and currency for anything printed. */
export function letterheadPath(branchId: number): string {
  return `/api/branch/${branchId}/letterhead`;
}

/** `GET /api/item-unit/{id}` — one garment: its design, configuration and serial. */
export function itemUnitPath(unitId: number): string {
  return `/api/item-unit/${unitId}`;
}

/** `GET /api/item-unit/{id}/rate-card` — the ladder that prices one garment, for its hang tag. */
export function garmentRateCardPath(unitId: number): string {
  return `/api/item-unit/${unitId}/rate-card`;
}

/** `GET /api/availability/search` — one design's garments over the possession dates, free or not. */
export function availabilitySearchPath(itemId: number, fromDate: string, toDate: string): string {
  return `/api/availability/search?${new URLSearchParams({ itemId: String(itemId), fromDate, toDate }).toString()}`;
}

/**
 * `GET /api/availability/catalogue` — a page of stocked designs, each with all its garments over the
 * possession dates. The one call that replaces a search per design; empty filters are left off.
 */
export function availabilityCataloguePath(filters: {
  fromDate: string;
  toDate: string;
  search?: string | undefined;
  categoryId?: number | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}): string {
  const params = new URLSearchParams({ fromDate: filters.fromDate, toDate: filters.toDate });

  if (filters.search?.trim()) {
    params.set('search', filters.search.trim());
  }

  if (filters.categoryId !== undefined) {
    params.set('categoryId', String(filters.categoryId));
  }

  if (filters.page !== undefined) {
    params.set('page', String(filters.page));
  }

  if (filters.pageSize !== undefined) {
    params.set('pageSize', String(filters.pageSize));
  }

  return `/api/availability/catalogue?${params.toString()}`;
}

/** `GET /api/item` — the designs a branch carries, which the booking rail searches across. */
export function itemsPath(
  filters: { search?: string; page?: number; pageSize?: number } = {},
): string {
  const params = new URLSearchParams({
    page: String(filters.page ?? 1),
    pageSize: String(filters.pageSize ?? 25),
  });

  if (filters.search) {
    params.set('search', filters.search);
  }

  return `/api/item?${params.toString()}`;
}

/**
 * `GET /api/item/cards` — every design as a picture card with its garments at this branch, stocked
 * or not (the phone's list of designs).
 */
export function designCardsPath(filters: ItemFilters = {}): string {
  const { search, categoryId, includeInactive = false, page = 1, pageSize = 30 } = filters;
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  if (search) {
    params.set('search', search);
  }

  if (categoryId !== undefined) {
    params.set('categoryId', String(categoryId));
  }

  if (includeInactive) {
    params.set('includeInactive', 'true');
  }

  return withParams('/api/item/cards', params);
}

/** `GET /api/customer` — the customer search a booking starts from. */
export function customersPath(
  filters: { search?: string; page?: number; pageSize?: number } = {},
): string {
  const params = new URLSearchParams({
    page: String(filters.page ?? 1),
    pageSize: String(filters.pageSize ?? 25),
  });

  if (filters.search) {
    params.set('search', filters.search);
  }

  return `/api/customer?${params.toString()}`;
}

/** `GET /api/item-category` — the category tree, live ones only unless asked. */
/**
 * `GET /api/item-category/{id}/axes` — what a garment of this category must carry, for a design
 * not created yet (the stock import matches sizes and colours before the design exists).
 */
export function categoryAxesPath(categoryId: number): string {
  return `/api/item-category/${categoryId}/axes`;
}

/** `GET /api/stock-import` — this branch's recent spreadsheet imports, newest first. */
export function stockImportsPath(): string {
  return '/api/stock-import';
}

export function itemCategoriesPath(includeInactive = false): string {
  return `/api/item-category${includeInactive ? '?includeInactive=true' : ''}`;
}

/** `GET /api/return-inspection/garment/{id}` — one garment's checklist and what was said last time. */
export function garmentInspectionPath(rentalOrderItemId: number): string {
  return `/api/return-inspection/garment/${rentalOrderItemId}`;
}

/** `GET /api/report/garment-movement` — report A1: what moved each shop day, both ends included. */
export function garmentMovementPath(from: string, to: string): string {
  return withParams('/api/report/garment-movement', new URLSearchParams({ from, to }));
}

/** `GET /api/report/garment-movement/day` — report A1, one day opened: every garment that moved. */
export function garmentMovementDayPath(day: string): string {
  return withParams('/api/report/garment-movement/day', new URLSearchParams({ day }));
}

/**
 * `GET /api/report/stock-summary` — report B2: the garments as they stand. With `atCost` the read
 * carries the value at cost, and needs `Report:ViewFinancials`.
 */
export function stockSummaryPath(atCost: boolean): string {
  return atCost ? '/api/report/stock-summary/at-cost' : '/api/report/stock-summary';
}

/** `GET /api/report/rental-revenue` — report C2: what was earned over a range, a row per grain. */
export function rentalRevenuePath(
  from: string,
  to: string,
  grain: 'Day' | 'Month' | 'Year',
): string {
  return withParams('/api/report/rental-revenue', new URLSearchParams({ from, to, grain }));
}
